import { type SxProps, type Theme } from "@mui/material/styles";

export const consentDialogStyles: SxProps<Theme> = (theme) => ({
  "--consent-space-2": "0.5rem",
  "--consent-space-3": "0.75rem",
  "--consent-space-4": "1rem",
  "--consent-space-5": "1.25rem",
  "--consent-space-7-5": "1.875rem",
  "& :where(.consent-platform, .consent-platform-confirm) svg": {
    width: "1em",
    height: "1em",
    fontSize: "1.25rem",
  },
  "&.consent-platform-dialog .MuiBackdrop-root, &.consent-platform-confirm-dialog .MuiBackdrop-root":
    {
      background: `color-mix(in srgb, ${theme.palette.blue.main} 72%, transparent)`,
      backdropFilter: "blur(0.1875rem)",
    },
  "&.consent-platform-dialog .MuiDialog-paper": {
    width: "47.5rem",
    maxWidth: "calc(100% - 2.5rem)",
    maxHeight: "calc(100dvh - 2.5rem)",
    margin: "var(--consent-space-5)",
    borderRadius: "0.75rem",
  },
  "& .consent-platform": {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    color: theme.palette.blue.main,
    fontSize: "0.8125rem",
    lineHeight: 1.5,
    background: theme.palette.background.paper,
  },
  "& .consent-platform *, & .consent-platform-confirm *": {
    boxSizing: "border-box",
  },
  "& .consent-platform p": {
    margin: 0,
  },
  "& .consent-platform-scroll": {
    overflowY: "auto",
    overscrollBehavior: "contain",
  },
  "& .consent-platform-header": {
    padding: "var(--consent-space-5) var(--consent-space-7-5)",
    color: theme.palette.background.paper,
    background: theme.palette.primary.main,
    borderBottom: `0.0625rem solid ${theme.palette.gray.deep}`,
  },
  "& .consent-platform-header h1": {
    fontSize: "1.25rem",
    lineHeight: 1.3,
    margin: "var(--consent-space-3) 0",
    fontWeight: 700,
  },
  "& .consent-platform-header p": {
    fontSize: "0.875rem",
  },
  "& .consent-platform-badge": {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    borderRadius: "1.25rem",
    padding: "0.125rem var(--consent-space-3)",
    background: "rgb(255 255 255 / 18%)",
    textTransform: "uppercase",
    fontSize: "0.625rem",
    fontWeight: 800,
  },
  "& .consent-platform-badge svg": {
    fontSize: "0.8125rem",
  },
  "& .consent-platform-body": {
    padding: "var(--consent-space-4) var(--consent-space-7-5) 1.5rem",
  },
  "& .consent-platform-intro": {
    display: "flex",
    alignItems: "flex-start",
    gap: "var(--consent-space-3)",
    padding: "var(--consent-space-4) var(--consent-space-4)",
    borderLeft: `0.1875rem solid ${theme.palette.orange.contrastText ?? theme.palette.text.primary}`,
    borderRadius: "0.25rem",
    background: `color-mix( in srgb, ${theme.palette.orange.light ?? theme.palette.primary.light} 45%, ${theme.palette.background.paper} )`,
    color: theme.palette.gray.dark,
    borderColor: theme.palette.gray.deep,
    borderLeftColor:
      theme.palette.orange.contrastText ?? theme.palette.text.primary,
  },
  "& .consent-platform-accept-all": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--consent-space-4)",
    marginTop: "var(--consent-space-5)",
    padding: "var(--consent-space-4) var(--consent-space-4)",
    border: `0.125rem solid ${theme.palette.green.main}`,
    borderRadius: "0.25rem",
    background: theme.palette.green.light ?? theme.palette.primary.light,
    boxShadow: "none",
    borderColor: theme.palette.green.main,
  },
  "& .consent-platform-accept-all .consent-platform-badge": {
    marginBottom: "0.375rem",
    background: theme.palette.green.main,
    color: theme.palette.background.paper,
    fontSize: "0.5625rem",
    padding: "0.125rem var(--consent-space-2)",
    borderColor: theme.palette.green.main,
    boxShadow: "none",
  },
  "& .consent-platform-accept-all strong": {
    display: "block",
  },
  "& .consent-platform-accept-all p": {
    fontSize: "0.6875rem",
    marginTop: "0.125rem",
  },
  "& .consent-platform-button": {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--consent-space-3)",
    cursor: "pointer",
    padding: "var(--consent-space-3) var(--consent-space-5)",
    minHeight: "2.75rem",
    borderRadius: "0.25rem",
    border: "0.125rem solid transparent",
    font: "inherit",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  "& .consent-platform-button-accept": {
    background: theme.palette.green.main,
    color: theme.palette.background.paper,
    borderColor: theme.palette.green.main,
    boxShadow: "none",
    flexShrink: 0,
  },
  "& .consent-platform-button-primary": {
    background: theme.palette.primary.main,
    color: theme.palette.background.paper,
    boxShadow: "none",
  },
  "& .consent-platform-button-primary:hover": {
    background: theme.palette.secondary.dark,
  },
  "& .consent-platform-button-primary:disabled": {
    cursor: "wait",
    opacity: 0.6,
  },
  "& .consent-platform-button-accept:hover": {
    background: `color-mix(in srgb, ${theme.palette.green.main} 85%, black)`,
    borderColor: theme.palette.green.main,
  },
  "& .consent-platform-button small": {
    display: "block",
    textTransform: "none",
    fontWeight: 400,
    fontSize: "0.6875rem",
  },
  "& .consent-platform button:focus-visible, & .consent-platform input:focus-visible, & .consent-platform-confirm button:focus-visible":
    {
      outline: `0.1875rem solid ${theme.palette.blue.focus}`,
      outlineOffset: "0.1875rem",
    },
  "& .consent-platform-divider": {
    display: "flex",
    alignItems: "center",
    gap: "var(--consent-space-3)",
    padding: "var(--consent-space-4) 0",
    textAlign: "center",
    textTransform: "uppercase",
    fontSize: "0.6875rem",
    fontWeight: 800,
    letterSpacing: "0.05rem",
    color: theme.palette.gray.dark,
  },
  "& .consent-platform-divider::before, & .consent-platform-divider::after": {
    content: '""',
    height: "0.0625rem",
    background: theme.palette.gray.deep,
    flex: 1,
  },
  "& .consent-platform-hint": {
    display: "flex",
    alignItems: "center",
    gap: "var(--consent-space-3)",
    padding: "var(--consent-space-3) var(--consent-space-4)",
    border: `0.0625rem dashed ${theme.palette.primary.main}`,
    borderRadius: "0.25rem",
    background: theme.palette.primary.light,
    fontSize: "0.75rem",
    borderColor: theme.palette.primary.main,
  },
  "& .consent-platform-purpose": {
    border: `0.0625rem solid ${theme.palette.gray.deep}`,
    borderRadius: "0.25rem",
    marginTop: "var(--consent-space-4)",
    overflow: "hidden",
    background: theme.palette.background.paper,
    borderColor: theme.palette.gray.deep,
  },
  '& .consent-platform-purpose[data-active="true"]': {
    borderColor: theme.palette.primary.main,
    background: theme.palette.background.paper,
  },
  "& .consent-platform-purpose-heading": {
    display: "flex",
    alignItems: "center",
    gap: "var(--consent-space-4)",
    padding: "var(--consent-space-4) var(--consent-space-5)",
  },
  "& .consent-platform-purpose-details": {
    display: "flex",
    flex: 1,
    alignItems: "center",
    gap: "var(--consent-space-4)",
    textAlign: "left",
    color: "inherit",
    border: 0,
    padding: 0,
    background: "none",
    font: "inherit",
  },
  "& .consent-platform-purpose-icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "2rem",
    height: "2rem",
    background: theme.palette.cyan.main,
    color: theme.palette.secondary.dark,
    borderRadius: "50%",
  },
  "& .consent-platform-purpose-icon svg": {
    fontSize: "1rem",
  },
  "& .consent-platform-purpose-description": {
    display: "block",
    fontSize: "0.75rem",
    marginTop: "0.125rem",
    color: theme.palette.gray.dark,
  },
  "& .consent-platform-switch": {
    appearance: "none",
    width: "2.625rem",
    height: "1.5rem",
    border: 0,
    borderRadius: "1.25rem",
    background: theme.palette.gray.deep,
    cursor: "pointer",
    flexShrink: 0,
    margin: 0,
    position: "relative",
  },
  "& .consent-platform-switch::after": {
    content: '""',
    position: "absolute",
    top: "0.1875rem",
    left: "0.1875rem",
    height: "1.125rem",
    width: "1.125rem",
    background: "white",
    borderRadius: "50%",
    boxShadow: "0 0.0625rem 0.1875rem rgb(0 0 0 / 16%)",
    transition: "transform 150ms",
  },
  "& .consent-platform-switch:checked": {
    background: theme.palette.primary.main,
    color: theme.palette.background.paper,
  },
  "& .consent-platform-switch:checked::after": {
    transform: "translateX(1.125rem)",
  },
  "& .consent-platform-purpose > div + div": {
    padding: "var(--consent-space-4) var(--consent-space-5)",
    borderTop: `0.0625rem solid ${theme.palette.gray.deep}`,
    background: theme.palette.background.paper,
    borderColor: theme.palette.gray.deep,
  },
  '& .consent-platform-purpose[data-active="true"] .consent-platform-purpose-icon':
    {
      background: theme.palette.primary.main,
      color: theme.palette.background.paper,
    },
  "& .consent-platform-channels": {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "var(--consent-space-2)",
    minWidth: 0,
    margin: 0,
    padding: "var(--consent-space-3) 0 0",
    border: 0,
  },
  "& .consent-platform-channels legend": {
    display: "flex",
    alignItems: "center",
    gap: "var(--consent-space-3)",
    width: "100%",
    padding: 0,
    lineHeight: 1.5,
    fontSize: "0.6875rem",
    letterSpacing: "0.0375rem",
    textTransform: "uppercase",
    fontWeight: 700,
    color: theme.palette.gray.dark,
  },
  "& .consent-platform-channel": {
    display: "flex",
    alignItems: "center",
    gap: "var(--consent-space-3)",
    minHeight: "2.5rem",
    padding: "var(--consent-space-3) var(--consent-space-3)",
    border: `0.125rem solid ${theme.palette.gray.deep}`,
    borderRadius: "0.25rem",
    background: theme.palette.background.paper,
    color: theme.palette.secondary.dark,
    fontWeight: 700,
    cursor: "pointer",
    fontSize: "0.75rem",
    borderColor: theme.palette.gray.deep,
  },
  "& .consent-platform-channel:has(input:checked)": {
    borderColor: theme.palette.primary.main,
    background: theme.palette.primary.light,
  },
  "& .consent-platform-channel input": {
    accentColor: theme.palette.primary.main,
    width: "1rem",
    height: "1rem",
    margin: 0,
    flexShrink: 0,
  },
  "& .consent-platform-channel svg": {
    fontSize: "1rem",
    flexShrink: 0,
    color: theme.palette.primary.main,
  },
  "& .consent-platform-channel small": {
    display: "inline",
    marginLeft: "0.25rem",
    fontSize: "0.625rem",
    fontWeight: 400,
    fontStyle: "italic",
  },
  "& .consent-platform-channels:disabled": {
    opacity: 0.5,
  },
  "& .consent-platform-tip": {
    display: "flex",
    alignItems: "center",
    gap: "var(--consent-space-3)",
    padding: "0.375rem var(--consent-space-4)",
    marginTop: "var(--consent-space-4)",
    border: `0.0625rem solid ${theme.palette.gray.deep}`,
    borderLeft: `0.1875rem solid ${theme.palette.orange.contrastText ?? theme.palette.text.primary}`,
    borderRadius: "0.25rem",
    background: `color-mix( in srgb, ${theme.palette.orange.light ?? theme.palette.primary.light} 45%, ${theme.palette.background.paper} )`,
    fontSize: "0.75rem",
    lineHeight: 1.45,
    color: theme.palette.gray.dark,
    borderColor: theme.palette.gray.deep,
    borderLeftColor:
      theme.palette.orange.contrastText ?? theme.palette.text.primary,
  },
  "& .consent-platform-tip strong": {
    color: theme.palette.orange.contrastText ?? theme.palette.text.primary,
  },
  "& .consent-platform-tip-icon": {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "2rem",
    height: "2rem",
    borderRadius: "50%",
    background: theme.palette.orange.contrastText ?? theme.palette.text.primary,
    color: theme.palette.background.paper,
  },
  "& .consent-platform-tip-icon svg": {
    fontSize: "1rem",
  },
  "& .consent-platform-faq": {
    marginTop: "var(--consent-space-5)",
    padding: "var(--consent-space-4)",
    borderRadius: "0.375rem",
  },
  "& .consent-platform-faq h2": {
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.0375rem",
    margin: "0 0 var(--consent-space-3)",
  },
  "& .consent-platform-faq h3": {
    fontSize: "0.8125rem",
    margin: 0,
  },
  "& .consent-platform-faq details + details": {
    borderTop: `0.0625rem solid ${theme.palette.gray.deep}`,
    borderColor: theme.palette.gray.deep,
  },
  "& .consent-platform-faq summary": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--consent-space-3)",
    padding: "var(--consent-space-4) 0",
    listStyle: "none",
    cursor: "pointer",
  },
  "& .consent-platform-faq summary::-webkit-details-marker": {
    display: "none",
  },
  "& .consent-platform-faq summary:focus-visible": {
    outline: `0.1875rem solid ${theme.palette.blue.focus}`,
    outlineOffset: "0.125rem",
  },
  "& .consent-platform-faq summary svg": {
    fontSize: "1.125rem",
    flexShrink: 0,
  },
  "& .consent-platform-faq details[open] summary svg": {
    transform: "rotate(180deg)",
  },
  "& .consent-platform-faq details p": {
    paddingBottom: "var(--consent-space-4)",
  },
  "& .consent-platform-faq p": {
    fontSize: "0.75rem",
    color: theme.palette.gray.dark,
  },
  "& .consent-platform-legal": {
    borderTop: `0.0625rem solid ${theme.palette.gray.deep}`,
    marginTop: "var(--consent-space-5)",
    paddingTop: "var(--consent-space-4)",
    color: theme.palette.gray.dark,
    fontSize: "0.6875rem",
    borderColor: theme.palette.gray.deep,
  },
  "& .consent-platform-legal p + p": {
    marginTop: "var(--consent-space-3)",
  },
  "& .consent-platform-legal a": {
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
  "& .consent-platform-footer": {
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--consent-space-3)",
    padding: "var(--consent-space-3) var(--consent-space-7-5)",
    borderTop: `0.0625rem solid ${theme.palette.gray.deep}`,
    borderColor: theme.palette.gray.deep,
    background: theme.palette.background.paper,
  },
  "& .consent-platform-count": {
    display: "flex",
    alignItems: "center",
    gap: "0.375rem",
    color: theme.palette.gray.dark,
    fontSize: "0.75rem",
  },
  "& .consent-platform-count strong": {
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    background: theme.palette.cyan.main,
    borderRadius: "50%",
    width: "1.75rem",
    height: "1.75rem",
    color: theme.palette.secondary.dark,
  },
  "&.consent-platform-confirm-dialog .MuiDialog-paper": {
    width: "35rem",
    maxWidth: "calc(100% - 2rem)",
    maxHeight: "calc(100dvh - 2rem)",
    margin: "var(--consent-space-4)",
    borderRadius: "0.75rem",
  },
  "& .consent-platform-confirm": {
    color: theme.palette.blue.main,
    fontSize: "0.875rem",
    lineHeight: 1.5,
    background: theme.palette.background.paper,
  },
  "& .consent-platform-confirm header": {
    padding: "1.625rem var(--consent-space-7-5)",
    color: theme.palette.background.paper,
    background: theme.palette.orange.main,
  },
  "& .consent-platform-warning-icon": {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "3.25rem",
    height: "3.25rem",
    marginBottom: "var(--consent-space-4)",
    borderRadius: "50%",
    background: "color-mix(in srgb, currentColor 12%, transparent)",
  },
  "& .consent-platform-warning-icon svg": {
    fontSize: "1.75rem",
  },
  "& .consent-platform-confirm h2": {
    fontSize: "1.375rem",
    fontWeight: 700,
    margin: 0,
    lineHeight: 1.3,
  },
  "& .consent-platform-confirm header p": {
    margin: "var(--consent-space-4) 0 0",
    fontSize: "1rem",
  },
  "& .consent-platform-confirm-body": {
    padding: "1.375rem var(--consent-space-7-5) 1.625rem",
  },
  "& .consent-platform-confirm-body > strong": {
    fontSize: "0.9375rem",
    textTransform: "uppercase",
    color: theme.palette.gray.dark,
  },
  "& .consent-platform-confirm ul": {
    listStyle: "none",
    padding: 0,
    margin: "1.375rem 0 2rem",
  },
  "& .consent-platform-confirm li": {
    display: "flex",
    alignItems: "flex-start",
    gap: "var(--consent-space-3)",
  },
  "& .consent-platform-confirm li + li": {
    marginTop: "var(--consent-space-5)",
  },
  "& .consent-platform-confirm li > svg": {
    flexShrink: 0,
    color: theme.palette.danger.main,
    fontSize: "1.25rem",
    marginTop: "0.0625rem",
  },
  "& .consent-platform-confirm .consent-platform-button": {
    width: "100%",
    background: theme.palette.primary.main,
    boxShadow: "none",
    minHeight: "2.75rem",
    fontSize: "0.875rem",
    color: theme.palette.background.paper,
  },
  "& .consent-platform-confirm .consent-platform-button:hover": {
    background: theme.palette.secondary.dark,
  },
  "& .consent-platform-refuse": {
    display: "block",
    width: "100%",
    minHeight: "3rem",
    marginTop: "var(--consent-space-3)",
    padding: "var(--consent-space-3)",
    cursor: "pointer",
    color: theme.palette.gray.dark,
    border: `0.125rem solid ${theme.palette.gray.deep}`,
    borderRadius: "0.3125rem",
    background: theme.palette.background.paper,
    font: "inherit",
    fontWeight: 700,
    borderColor: theme.palette.gray.deep,
  },
  "& .consent-platform-refuse:hover": {
    background: theme.palette.blue.light ?? theme.palette.primary.light,
  },
  "& .consent-platform-header .consent-platform-badge": {
    background: "transparent",
    border: "0.0625rem solid color-mix(in srgb, currentColor 60%, transparent)",
  },
  "@media (max-width: 37.5rem)": {
    "&.consent-platform-dialog .MuiDialog-paper": {
      width: "100%",
      maxWidth: "100%",
      height: "100dvh",
      maxHeight: "100dvh",
      margin: 0,
      borderRadius: 0,
    },
    "& .consent-platform": {
      height: "100%",
    },
    "& .consent-platform-header": {
      padding: "var(--consent-space-5) var(--consent-space-4)",
    },
    "& .consent-platform-header h1": {
      fontSize: "1.125rem",
    },
    "& .consent-platform-body": {
      padding: "var(--consent-space-4) var(--consent-space-4)",
    },
    "& .consent-platform-accept-all": {
      flexDirection: "column",
      alignItems: "stretch",
    },
    "& .consent-platform-purpose-heading": {
      padding: "var(--consent-space-3)",
      gap: "var(--consent-space-2)",
    },
    "& .consent-platform-purpose-details": {
      gap: "var(--consent-space-2)",
    },
    "& .consent-platform-channels": {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
    "& .consent-platform-footer": {
      padding: "var(--consent-space-3) var(--consent-space-4)",
      flexWrap: "wrap",
      justifyContent: "center",
    },
    "& .consent-platform-footer .consent-platform-button": {
      width: "100%",
    },
    "& .consent-platform-confirm header, & .consent-platform-confirm-body": {
      padding: "var(--consent-space-5)",
    },
  },
  "@media (prefers-reduced-motion: reduce)": {
    "& .consent-platform-switch::after": {
      transition: "none",
    },
  },
});
